import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Chip, Skeleton, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BRAND_COLORS } from '../styles/theme';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { useAnalyticsAccess } from '../hooks/useAnalyticsAccess';
import type { DateRangePreset, KpiMetric } from '../types/analytics';
import AnalyticsChat from '../components/AnalyticsChat/AnalyticsChat';
import StrategyEditor from '../components/Analytics/StrategyEditor';
import GoalsManager from '../components/Analytics/GoalsManager';
import IssueCard from '../components/Analytics/IssueCard';
import RecommendationCard from '../components/Analytics/RecommendationCard';
import KpiCard from '../components/Analytics/KpiCard';
import { COMPARISON_LABELS } from '../components/Analytics/kpiCard.utils';
import AnalyticsCharts from '../components/Analytics/AnalyticsCharts';
import ActionLog from '../components/Analytics/ActionLog';
import DigestArchive from '../components/Analytics/DigestArchive';

const DATE_RANGE_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12mo', label: '12 months' },
];

export const WebsiteAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasAccess, loading: accessLoading } = useAnalyticsAccess();
  const {
    dateRange,
    setDateRange,
    loadAllData,
    loadInsightsData,
    loadStrategyLayer,
    createActionLogEntry,
    deleteActionLogEntry,
    overview,
    traffic,
    sources,
    pages,
    geography,
    devices,
    userTypes,
    insights,
    actionLog,
    digests,
    overviewLoading,
    trafficLoading,
    sourcesLoading,
    pagesLoading,
    geographyLoading,
    devicesLoading,
    userTypesLoading,
    insightsLoading,
    actionLogLoading,
    digestsLoading,
  } = useAnalyticsStore();

  useEffect(() => {
    if (!hasAccess) return;
    loadAllData();
    loadInsightsData();
    loadStrategyLayer();
  }, [hasAccess, loadAllData, loadInsightsData, loadStrategyLayer]);

  if (accessLoading) {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Skeleton variant="text" width={240} height={40} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 6 }, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_COLORS.forestGreen.main }}>
            Website Analytics
          </Typography>
        </Box>
        <Paper sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            You don't have access to website analytics.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ask an admin to add your email to the analytics allowlist.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ---- KPI cards data ----
  const kpiCards: { metric: KpiMetric; invertTrend?: boolean }[] = overview
    ? [
        { metric: overview.active_users },
        { metric: overview.sessions },
        { metric: overview.page_views },
        { metric: overview.bounce_rate, invertTrend: true },
        { metric: overview.avg_session_duration },
        { metric: overview.pages_per_session },
        { metric: overview.new_users },
        { metric: overview.returning_users },
        ...(overview.top_source ? [{ metric: overview.top_source }] : []),
      ]
    : [];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND_COLORS.forestGreen.main }}>
          Website Analytics
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          {DATE_RANGE_OPTIONS.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              variant={dateRange === opt.value ? 'filled' : 'outlined'}
              color={dateRange === opt.value ? 'primary' : 'default'}
              onClick={() => setDateRange(opt.value)}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {overviewLoading
          ? Array.from({ length: 8 }).map((_, i) => (
              <KpiCard
                key={i}
                metric={{ label: '', value: 0, previous_value: null, format: 'number' }}
                loading
              />
            ))
          : kpiCards.map((card, i) => (
              <KpiCard
                key={i}
                metric={card.metric}
                loading={false}
                invertTrend={card.invertTrend}
                comparisonLabel={COMPARISON_LABELS[dateRange]}
              />
            ))}
      </Box>

      {/* Charts Grid */}
      <AnalyticsCharts
        traffic={traffic}
        sources={sources}
        pages={pages}
        geography={geography}
        devices={devices}
        userTypes={userTypes}
        trafficLoading={trafficLoading}
        sourcesLoading={sourcesLoading}
        pagesLoading={pagesLoading}
        geographyLoading={geographyLoading}
        devicesLoading={devicesLoading}
        userTypesLoading={userTypesLoading}
      />

      {/* ---- Strategy & Goals (#522) ---- */}
      <StrategyEditor />
      <GoalsManager />

      {/* ---- Insights & Action Log Row ---- */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Identified Issues */}
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Identified Issues
            </Typography>
            {insights && (
              <Chip
                label={`Week of ${insights.week_start}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10 }}
              />
            )}
          </Box>
          {insightsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
          ) : insights && insights.issues.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {insights.issues.map((issue, i) => (
                <IssueCard
                  key={issue.insight_key ?? i}
                  issue={issue}
                  weekStart={insights.week_start}
                  feedbackWritable
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No issues generated yet. Run the weekly insights pipeline to populate.
            </Typography>
          )}
        </Paper>

        {/* Action Log */}
        <ActionLog
          actionLog={actionLog}
          actionLogLoading={actionLogLoading}
          createActionLogEntry={createActionLogEntry}
          deleteActionLogEntry={deleteActionLogEntry}
        />
      </Box>

      {/* Recommended Actions */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Recommended Actions
          </Typography>
          {insights && (
            <Chip
              label={`Week of ${insights.week_start}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10 }}
            />
          )}
        </Box>
        {insightsLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={24} width={`${80 - i * 10}%`} />
            ))}
          </Box>
        ) : insights && insights.recommendations.length > 0 ? (
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {insights.recommendations.map((rec, i) => (
              <RecommendationCard
                key={rec.insight_key ?? i}
                recommendation={rec}
                weekStart={insights.week_start}
                feedbackWritable
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No recommendations yet. Insights are generated weekly.
          </Typography>
        )}
      </Paper>

      {/* Weekly Insights Archive */}
      <DigestArchive digests={digests} digestsLoading={digestsLoading} />

      {/* Analytics Chat Agent */}
      <AnalyticsChat />
    </Box>
  );
};

export default WebsiteAnalyticsPage;
